// The arithmetic behind a chart's axes, kept apart from the drawing so it can
// be tested without a browser.
import { bookById, bookIndex, type Testament } from "@bibleunleashed/shared";

/**
 * Round ticks from 0 to at least `max` — 0, 50, 100, 150 rather than 0, 43,
 * 86, 129 — about `target` steps apart. The last is the top of the chart.
 * What is counted is occurrences, so a step is never less than one: a word
 * used once is not drawn against a tick at half a use.
 */
export function niceTicks(max: number, target = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const rough = max / target;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= rough)!);
  return Array.from({ length: Math.ceil(max / step) + 1 }, (_, i) => i * step);
}

/**
 * The canon's own divisions, labelling a chart that runs through a testament
 * book by book: thirty-nine columns have no room for thirty-nine names, and
 * where a word clusters — the Law, the Prophets — is what the reader is after.
 */
export const DIVISIONS: readonly { name: string; from: string; to: string }[] = [
  { name: "Law", from: "Gen", to: "Deut" },
  { name: "History", from: "Josh", to: "Esth" },
  { name: "Poetry", from: "Job", to: "Song" },
  { name: "Prophets", from: "Isa", to: "Mal" },
  { name: "Gospels and Acts", from: "Matt", to: "Acts" },
  { name: "Letters and Revelation", from: "Rom", to: "Rev" },
];

/**
 * Round years for a timeline's axis, from at or before `from` to at or after
 * `to`, about `target` steps apart. Years are signed, BC below zero, and a
 * step is never less than one year.
 */
export function yearTicks(from: number, to: number, target = 5): number[] {
  // A single moment still needs an axis either side of it.
  if (to <= from) [from, to] = [from - 1, to + 1];
  const rough = (to - from) / target;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= rough)!);
  const first = Math.floor(from / step) * step;
  const last = Math.ceil(to / step) * step;
  return Array.from({ length: Math.round((last - first) / step) + 1 }, (_, i) => first + i * step + 0);
}

/** "931 BC", "AD 30" — and the turn of the eras, which is no year at all. */
export const yearLabel = (year: number): string => (year < 0 ? `${-year} BC` : year === 0 ? "BC · AD" : `AD ${year}`);

/** A testament's divisions, each with how many books it spans. */
export function divisions(testament: Testament): { name: string; span: number }[] {
  return DIVISIONS.filter((d) => bookById(d.from)?.testament === testament).map((d) => ({
    name: d.name,
    span: bookIndex(d.to) - bookIndex(d.from) + 1,
  }));
}
